import React, { useState } from 'react';
import { Download, Eye, Paperclip, X } from 'lucide-react';
import type { FileAttachment } from '../../../types/kataBoardV2';
import { fileAttachmentApi } from '../../../services/kataBoardV2';
import FileAttachmentPreviewModal from '../../common/FileAttachmentPreviewModal';

export const PDCACycleAttachmentLinks: React.FC<{ attachments: FileAttachment[] }> = ({ attachments }) => {
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<FileAttachment | null>(null);

  const download = async (attachment: FileAttachment) => {
    setError('');
    setDownloadingId(attachment.id);
    try {
      const blob = await fileAttachmentApi.download(attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError('Could not download the file. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-2">
      {attachments.map(attachment => (
        <div key={attachment.id} className="flex items-center gap-2 text-sm">
          <Paperclip className="h-4 w-4 shrink-0 text-gray-400" />
          <a
            href={attachment.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 truncate text-blue-600 hover:underline"
            title={attachment.original_filename}
          >
            {attachment.original_filename}
          </a>
          {(attachment.is_image || attachment.is_pdf) && (
            <button
              type="button"
              onClick={() => setPreviewFile(attachment)}
              aria-label={`Preview ${attachment.original_filename}`}
              title="Preview attachment"
              className="shrink-0 text-gray-500 hover:text-blue-600"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => download(attachment)}
            disabled={downloadingId !== null}
            aria-label={`Download ${attachment.original_filename}`}
            className="shrink-0 text-gray-500 hover:text-blue-600 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      ))}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {previewFile && <FileAttachmentPreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

interface Props {
  attachments?: FileAttachment[];
  removedIds?: number[];
  files: File[];
  onFilesChange: (files: File[]) => void;
  onRemove?: (id: number) => void;
  disabled?: boolean;
}

const PDCACycleAttachments: React.FC<Props> = ({
  attachments = [], removedIds = [], files, onFilesChange, onRemove, disabled = false,
}) => {
  const [error, setError] = useState('');
  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-4">
      <label className="block text-sm font-medium text-gray-700">
        Attachments
        <input
          type="file"
          multiple
          disabled={disabled}
          className="mt-2 block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-700 disabled:opacity-50"
          onChange={event => {
            const selected = Array.from(event.target.files || []);
            const oversized = selected.find(file => file.size > 10 * 1024 * 1024);
            if (oversized) {
              setError(`${oversized.name} exceeds the 10 MB limit. Select a smaller file.`);
            } else {
              setError('');
              onFilesChange([...files, ...selected]);
            }
            event.target.value = '';
          }}
        />
      </label>
      <p className="text-xs text-gray-500">Optional. Up to 10 MB per file. Files belong to this cycle and changes are applied when you save.</p>
      {attachments.filter(attachment => !removedIds.includes(attachment.id)).map(attachment => (
        <div key={attachment.id} className="flex items-center justify-between gap-3 rounded bg-gray-50 p-2">
          <div className="min-w-0 flex-1"><PDCACycleAttachmentLinks attachments={[attachment]} /></div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onRemove?.(attachment.id)}
            aria-label={`Remove ${attachment.original_filename}`}
            className="text-gray-500 hover:text-red-600 disabled:opacity-50"
          ><X className="h-4 w-4" /></button>
        </div>
      ))}
      {files.map((file, index) => (
        <div key={index} className="flex items-center justify-between gap-3 rounded bg-blue-50 p-2 text-sm">
          <span className="min-w-0 truncate text-gray-700">{file.name} <span className="text-xs text-blue-600">(new)</span></span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onFilesChange(files.filter((_, fileIndex) => fileIndex !== index))}
            aria-label={`Remove ${file.name}`}
            className="text-gray-500 hover:text-red-600 disabled:opacity-50"
          ><X className="h-4 w-4" /></button>
        </div>
      ))}
      {removedIds.length > 0 && <p className="text-xs text-gray-500">{removedIds.length} attachment(s) will be removed when you save. Cancel to keep them.</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default PDCACycleAttachments;
